import React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import languages from '../../constants/DefaultLang';
import config from '../../config';

const HelmetMeta = (props) => {
  const { seo = {}, publishDate, pageClass = '', i18n, siteConfig, status } = props;
  const curLang = i18n.locale;
  const { data = {} } = siteConfig || {};
  const { availableLanguages, defaultLanguage } = data;

  const getURLByLang = (lang) => {
    const curRootPath = `${config.routePathTo}/${curLang}`;
    const pathname =
      window.location.pathname === curRootPath ||
      window.location.pathname === config.routePathTo ||
      window.location.pathname === `${config.routePathTo}/`
        ? `${config.routePathTo}/${lang}`
        : window.location.pathname.replace(curRootPath, `${config.routePathTo}/${lang}`);

    const link = `${window.location.host}${pathname}`;

    return link;
  };

  const meta = [];

  // CMS meta data

  if (status) {
    meta.push(<meta name="prerender-status-code" content={status} />);
  }

  // publishDate
  if (publishDate) {
    meta.push(
      <meta
        key="publishDate"
        name="publish date"
        content={new Date(Number(props.publishDate)).toISOString()}
      />,
    );
  }

  // type
  if (seo.seo_type && seo.seo_type !== '') {
    meta.push(<meta key="og:type" property="og:type" content={seo.seo_type} />);
  }

  // sitename
  if (seo.seo_sitename && seo.seo_sitename !== '') {
    meta.push(<meta key="og:site_name" property="og:site_name" content={seo.seo_sitename} />);
  }

  // img
  if (seo.seo_image && seo.seo_image !== '') {
    meta.push(<meta key="image" itemProp="image" content={seo.seo_image} />);
    meta.push(<meta key="og:image" property="og:image" content={seo.seo_image} />);
    meta.push(
      <meta key="og:image:secure_url" property="og:image:secure_url" content={seo.seo_image} />,
    );
    meta.push(<meta key="og:image:width" property="og:image:width" content="1200" />);
    meta.push(<meta key="og:image:height" property="og:image:height" content="630" />);
  }
  if (seo.seo_image_alt && seo.seo_image_alt !== '') {
    meta.push(<meta key="og:image:alt" property="og:image:alt" content={seo.seo_image_alt} />);
  }

  // title
  if (seo.seo_title && seo.seo_title !== '') {
    meta.push(<meta key="name" itemProp="name" content={seo.seo_title} />);
    meta.push(<meta key="og:title" property="og:title" content={seo.seo_title} />);
  }

  // desc
  if (seo.seo_desc && seo.seo_desc !== '') {
    meta.push(<meta key="description" name="description" content={seo.seo_desc} />);
    meta.push(<meta key="description" itemProp="description" content={seo.seo_desc} />);
    meta.push(<meta key="twitter:card" name="twitter:card" content={seo.seo_desc} />);
    meta.push(<meta key="og:description" property="og:description" content={seo.seo_desc} />);
  }

  // keywords
  if (seo.seo_keywords && seo.seo_keywords !== '') {
    meta.push(<meta key="keywords" name="keywords" content={seo.seo_keywords} />);
  }

  // industry
  if (seo.seo_industry && seo.seo_industry !== '') {
    meta.push(<meta key="industry" name="industry" content={seo.seo_industry} />);
  }

  // region
  if (seo.seo_region && seo.seo_region !== '') {
    meta.push(<meta key="region" name="region" content={seo.seo_region} />);
  }

  // Canonical
  meta.push(<link key="canonical" rel="canonical" href={window.location.href} />);
  meta.push(<meta key="meta-twitter:site" name="twitter:site" content={window.location.href} />);
  meta.push(<meta key="meta-og:url" property="og:url" content={window.location.href} />);

  // robot meta tag
  meta.push(<meta key="robots" name="robots" content="noodp, noydir" />);

  // Google hreflang
  Object.keys(languages || {})
    .filter((v) => availableLanguages.indexOf(v) > -1)
    .forEach((v, i) => {
      meta.push(
        <link
          key={`alt_${v}`}
          rel="alternate"
          hrefLang={languages[v].iso}
          href={getURLByLang(v)}
        />,
      );
    });

  meta.push(
    <link
      key="alt_x-default"
      rel="alternate"
      hrefLang="x-default"
      href={getURLByLang(defaultLanguage)}
    />,
  );

  // Knowledge Graphic
  meta.push(
    <script key="kGraphic" type="application/ld+json">
      {`
          { "@context" : "http://schema.org",
            "@type" : "Organization",
            "legalName" : "Hong Kong Trade Development Council",
            "url": "https://www.hktdc.com",
            "contactPoint" : [{
              "@type" : "ContactPoint",
              "telephone" : "(852)1830668",
              "contactType" : "customer service"
            }],
            "logo": "https://home.hktdc.com/images/logo-with-tag.jpg",
            "sameAs" : [ 
              "http://www.facebook.com/hktdc",
              "http://www.twitter.com/hktdc",
              "https://www.youtube.com/user/hktdc",
              "https://www.linkedin.com/company/hong-kong-trade-development-council/?originalSubdomain=hk"]
          }
        `}
    </script>,
  );

  return (
    <Helmet>
      {curLang && <html lang={languages[curLang].iso} />}
      {pageClass && <body className={pageClass} />}
      {meta}
    </Helmet>
  );
};

const mapStateToProps = (state) => ({
  i18n: state.i18n,
  siteConfig: state.siteConfig,
});

const connectStore = connect(mapStateToProps, null, null, {
  pure: true,
})(HelmetMeta);

export { HelmetMeta as HelmetMetaComponent }; // <- For component Unit Test
export default connectStore;
